import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const location = useLocation();

  const initAuth = useCallback(async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!location.pathname.startsWith('/portal') && !location.pathname.startsWith('/task')) {
      setLoading(false);
      return;
    }

    setLoading(true);
    initAuth();
  }, [location.pathname, initAuth]);

  const login = useCallback(async (email, password) => {
    const response = await apiClient.post('/auth/login', {
      email,
      password
    });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      setUser(null);
    }
  }, []);

  const changePassword = useCallback(async (oldPassword, newPassword) => {
    await apiClient.post('/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword
    });
    setUser((prev) => prev ? { ...prev, must_change_password: false } : prev);
  }, []);

  const value = {
    user,
    loading,
    login,
    logout,
    changePassword,
    isAdmin: user?.role === 'admin',
    isEmployee: user?.role === 'employee',
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
